import React, { useState, useContext } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useMenuPermissions } from '../../contexts/MenuPermissionsContext';

// Icons
import { 
  HomeIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowLeftOnRectangleIcon,
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  BellAlertIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UserIcon,
  CubeIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser, logout } = useContext(AuthContext);
  const { hasMenuPermission, loading: permissionsLoading } = useMenuPermissions();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Menu items configuration
  const menuItems = [
    // { path: '/', name: 'Dashboard', icon: HomeIcon }, // Hidden - Dashboard still accessible at /dashboard
    { path: '/my-dashboard', name: 'My Dashboard', icon: BellAlertIcon },
    { path: '/my-work', name: 'My Work', icon: ClipboardDocumentListIcon },
    { path: '/clients', name: 'Clients', icon: UserGroupIcon },
    { path: '/vendors', name: 'Vendors', icon: BuildingOffice2Icon },
    { path: '/projects', name: 'Projects', icon: DocumentTextIcon },
    { path: '/tasks', name: 'Tasks', icon: CheckCircleIcon },
    { path: '/company', name: 'Company Page', icon: BuildingOfficeIcon },
    { path: '/price-book', name: 'Price Book', icon: CurrencyDollarIcon },
    { path: '/products', name: 'Products', icon: CubeIcon },
    { path: '/imports', name: 'Data Imports', icon: ArrowUpTrayIcon },
    { path: '/views', name: 'Views', icon: ChartBarIcon },
    { path: '/audit-reporting', name: 'Audit & Reporting', icon: DocumentChartBarIcon },
    { path: '/security', name: 'Security', icon: ShieldCheckIcon },
    { path: '/settings', name: 'Settings', icon: 'settings' } // Custom settings icon
  ];

  // Render a menu item if the user has permission
  const renderMenuItem = (item, isMobile = false) => {
    // If permissions are loading, show all menu items
    if (permissionsLoading) {
      // Regular rendering, just without permission check
    } else {
      // Check permissions and skip if not allowed
      if (!hasMenuPermission(item.path)) {
        return null;
      }
    }

    // Settings icon needs special handling
    const IconComponent = item.icon === 'settings' ? null : item.icon;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => console.log(`Clicked menu item: ${item.name}, path: ${item.path}`)}
        className={`${isActive(item.path) ? 'bg-indigo-900 text-white' : 'text-gray-200 hover:bg-gray-700 hover:text-white'} group flex items-center px-2 py-2 text-${isMobile ? 'base' : 'sm'} font-medium rounded-md`}
      >
        {IconComponent ? (
          <IconComponent 
            className={`${isActive(item.path) ? 'text-indigo-300' : 'text-gray-400 group-hover:text-gray-200'} mr-${isMobile ? '4' : '3'} h-6 w-6`} 
          />
        ) :
        <svg className={`${isActive(item.path) ? 'text-indigo-300' : 'text-gray-400 group-hover:text-gray-200'} mr-${isMobile ? '4' : '3'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        }
        <span className="flex-1">{item.name}</span>
        {permissionsLoading && (
          <span className="h-1.5 w-1.5 bg-gray-500 rounded-full animate-pulse"></span>
        )}
      </Link>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-900">
      {/* Sidebar for mobile */}
      <div 
        className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`}
        onClick={() => setSidebarOpen(false)}
      >
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" aria-hidden="true"></div>
        
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-xl font-bold text-indigo-300">FrameWorX</h1>
            </div>
            
            <nav className="mt-5 px-2 space-y-1">
              {menuItems.map(item => renderMenuItem(item, true))}
            </nav>
          </div>
          
          <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
            <div className="flex-shrink-0 group block">
              <div className="flex items-center">
                <Link to="/profile" className="flex items-center hover:opacity-80 transition-opacity">
                  <div>
                    {currentUser?.picture_url ? (
                      <img 
                        src={currentUser.picture_url} 
                        alt={currentUser.name} 
                        className="h-9 w-9 rounded-full"
                      />
                    ) : (
                      <div className="inline-block h-9 w-9 rounded-full bg-gray-700 text-gray-200 flex items-center justify-center hover:bg-gray-600 transition-colors">
                        <UserIcon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-medium text-gray-200 hover:text-white transition-colors">
                      {currentUser?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-400">View Profile</p>
                  </div>
                </Link>
                <div className="ml-auto">
                  <button 
                    onClick={handleLogout}
                    className="text-sm font-medium text-indigo-300 hover:text-indigo-200 cursor-pointer transition-colors"
                    title="Logout"
                  >
                    <ArrowLeftOnRectangleIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-700 bg-gray-800">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-2xl font-bold text-indigo-300">FrameWorX</h1>
              </div>
              
              <nav className="mt-5 flex-1 px-2 bg-gray-800 space-y-1">
                {menuItems.map(item => renderMenuItem(item, false))}
              </nav>
            </div>
            
            <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <Link to="/profile" className="flex items-center hover:opacity-80 transition-opacity flex-1">
                    <div>
                      {currentUser?.picture_url ? (
                        <img 
                          src={currentUser.picture_url} 
                          alt={currentUser.name} 
                          className="h-9 w-9 rounded-full"
                        />
                      ) : (
                        <div className="inline-block h-9 w-9 rounded-full bg-gray-700 text-gray-200 flex items-center justify-center hover:bg-gray-600 transition-colors">
                          <UserIcon className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-200 hover:text-white transition-colors">
                        {currentUser?.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-400">View Profile</p>
                    </div>
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="ml-3 text-xs font-medium text-indigo-300 hover:text-indigo-200 cursor-pointer transition-colors"
                    title="Logout"
                  >
                    <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile header */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
        
        {/* Main content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-8xl mx-auto pl-10 pr-4 sm:pl-16 sm:pr-6 md:pl-24 md:pr-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 